import React, { useState, useEffect } from "react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import Creatable from "react-select/creatable";
import AsyncCreatableSelect from "react-select/async-creatable";

const DropdownList = ({
  id,
  input: { onChange, value, name },
  meta,
  label,
  options = [],
  isMulti = false,
  isCreatable = false,
  boolean = false,
  numeric = false,
  isClearable = true,
  menuPosition = "absolute",
  placeholder = "Select...",
  tabIndex = 0,
  menuPlacement = "auto",
  formatOptionLabel,
  curriedApiCallback = () => null
}) => {
  const [hasMounted, setHasMounted] = useState(false);
  const [optionsToDisplay, setOptionsToDisplay] = useState(options || []);
  const apiCallback = curriedApiCallback(setOptionsToDisplay);
  const hasApiCallback = apiCallback !== null;

  const optionsCheck = JSON.stringify(options);
  useEffect(() => {
    if (hasMounted !== true) setHasMounted(true);
    if (!hasApiCallback) setOptionsToDisplay(options);
  }, [optionsCheck, hasMounted, hasApiCallback]);

  // prevent server-side rendering issues
  if (!hasMounted) return null;

  const handleChange = (userData) => {
    if (userData === null) {
      onChange(null);
      return;
    }
    if (!("value" in userData)) {
      return;
    }
    onChange(userData.value);
  };

  const handleChangeMulti = (userData) => {
    onChange(userData.map((data) => data.value));
  };

  const handleChangeMultiCreatable = (userData) => {
    setOptionsToDisplay(
      optionsToDisplay.concat(
        userData
          .filter(({ __isNew__ }) => __isNew__ === true)
          .map(({ label, value }) => ({ label, value }))
      )
    );
    onChange(userData.map((data) => data.value));
  };

  const handleChangeCreatable = (userData) => {
    if (!userData || !userData.value) {
      onChange(null);
      return;
    }
    setOptionsToDisplay(
      optionsToDisplay.concat(
        [userData]
          .filter(({ __isNew__ }) => __isNew__ === true)
          .map(({ label, value }) => ({ label, value }))
      )
    );
    onChange(userData.value);
  };

  let selectedValue =
    boolean === true
      ? value === true || value == "t"
        ? "t"
        : value === false || value == "f"
        ? "f"
        : value
      : numeric === true
      ? value.toString()
      : value;

  if (isMulti) {
    if (selectedValue && !Array.isArray(selectedValue)) {
      selectedValue = [selectedValue];
    }
    if (typeof selectedValue === "undefined" || selectedValue === null) {
      selectedValue = [];
    }
  }

  const selected =
    isMulti === false
      ? optionsToDisplay.filter((el) => el.value === selectedValue)[0] || null
      : optionsToDisplay.filter((el) =>
          el.value
            ? selectedValue.indexOf(el.value) != -1
            : selectedValue.indexOf(el) != -1
        );

  return (
    <div
      className={`react-select-container ${
        meta.touched && meta.invalid ? " has-error" : ""
      }`}
    >
      {label && <label className="form-label">{label}</label>}
      {isCreatable ? (
        apiCallback === null ? (
          <Creatable
            isClearable={isClearable}
            isMulti={isMulti}
            onChange={
              isMulti ? handleChangeMultiCreatable : handleChangeCreatable
            }
            options={optionsToDisplay}
            value={selected}
            menuPosition={menuPosition}
            placeholder={placeholder}
            tabIndex={tabIndex}
            id={id || name}
          />
        ) : (
          <AsyncCreatableSelect
            isClearable={isClearable}
            isMulti={isMulti}
            onChange={
              isMulti ? handleChangeMultiCreatable : handleChangeCreatable
            }
            options={optionsToDisplay}
            value={selected}
            menuPosition={menuPosition}
            placeholder={placeholder}
            tabIndex={tabIndex}
            id={id || name}
            loadOptions={apiCallback}
          />
        )
      ) : apiCallback === null ? (
        <Select
          className="react-select-container"
          classNamePrefix="react-select"
          isClearable={isClearable}
          options={optionsToDisplay}
          onChange={isMulti ? handleChangeMulti : handleChange}
          value={selected}
          isMulti={isMulti}
          menuPosition={menuPosition}
          menuPlacement={menuPlacement}
          placeholder={placeholder}
          tabIndex={tabIndex}
          id={id || name}
          formatOptionLabel={formatOptionLabel}
        />
      ) : (
        <AsyncSelect
          className="react-select-container"
          classNamePrefix="react-select"
          isClearable={isClearable}
          options={optionsToDisplay}
          onChange={isMulti ? handleChangeMulti : handleChange}
          value={selected}
          isMulti={isMulti}
          menuPosition={menuPosition}
          menuPlacement={menuPlacement}
          placeholder={placeholder}
          tabIndex={tabIndex}
          id={id || name}
          loadOptions={apiCallback}
        />
      )}
      {meta.touched && meta.invalid && (
        <p className="bg-red-50 mt-2 text-sm text-red-700 py-2 px-4 rounded">
          {meta.error || meta.submitError}
        </p>
      )}
    </div>
  );
};

export default DropdownList;
